import React, { useMemo, useRef, useState } from "react";
import { Deal, PIPELINE_STAGES } from "@/hooks/useDealPipeline";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from "recharts";
import { format, startOfMonth, eachMonthOfInterval, subMonths, addMonths } from "date-fns";
import { DollarSign, TrendingUp, Target, Activity, FileImage, FileText, Loader2 } from "lucide-react";
import html2canvas from "html2canvas";
import html2pdf from "html2pdf.js";
import { useToast } from "@/hooks/use-toast";

interface DealStatisticsChartsProps {
  deals: Deal[];
}

const COLORS = [
  "#3b82f6",
  "#6366f1",
  "#8b5cf6",
  "#a855f7",
  "#d946ef",
  "#f59e0b",
  "#22c55e",
  "#06b6d4",
  "#f97316",
  "#10b981",
  "#14b8a6",
];

const STAGE_COLORS: Record<string, string> = {
  submission: "#3b82f6",
  pre_evaluation: "#6366f1",
  cim_creation: "#8b5cf6",
  payment_pending: "#a855f7",
  published: "#d946ef",
  lender_submission: "#f59e0b",
  nda_signed: "#22c55e",
  buyer_contact: "#06b6d4",
  first_meeting: "#f97316",
  loi_signed: "#10b981",
  closing: "#14b8a6",
};

export function DealStatisticsCharts({ deals }: DealStatisticsChartsProps) {
  const chartsRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  const [isExporting, setIsExporting] = useState(false);
  const [exportType, setExportType] = useState<"pdf" | "image" | null>(null);

  const exportAsImage = async () => {
    if (!chartsRef.current) return;
    setIsExporting(true);
    setExportType("image");
    
    try {
      const canvas = await html2canvas(chartsRef.current, {
        backgroundColor: "#ffffff",
        scale: 2,
        logging: false,
        useCORS: true,
      });
      
      const link = document.createElement("a");
      link.download = `deal-statistics-${format(new Date(), "yyyy-MM-dd")}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      
      toast({ title: "Export successful", description: "Statistics exported as PNG image" });
    } catch (error) {
      console.error("Export error:", error);
      toast({ title: "Export failed", description: "Could not export statistics", variant: "destructive" });
    } finally {
      setIsExporting(false);
      setExportType(null);
    }
  };

  const exportAsPDF = async () => {
    if (!chartsRef.current) return;
    setIsExporting(true);
    setExportType("pdf");
    
    try {
      const opt = {
        margin: 10,
        filename: `deal-statistics-${format(new Date(), "yyyy-MM-dd")}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, backgroundColor: "#ffffff", logging: false, useCORS: true },
        jsPDF: { unit: "mm", format: "a4", orientation: "landscape" as const },
      };
      
      await html2pdf().set(opt).from(chartsRef.current).save();
      toast({ title: "Export successful", description: "Statistics exported as PDF document" });
    } catch (error) {
      console.error("Export error:", error);
      toast({ title: "Export failed", description: "Could not export statistics", variant: "destructive" });
    } finally {
      setIsExporting(false);
      setExportType(null);
    }
  };

  const dealsByStage = useMemo(() => {
    return PIPELINE_STAGES.map(stage => {
      const count = deals.filter(d => d.current_stage === stage.stage).length;
      return { name: stage.label, stage: stage.stage, count, fill: STAGE_COLORS[stage.stage] || "#888" };
    });
  }, [deals]);

  const valueByStage = useMemo(() => {
    return PIPELINE_STAGES.map(stage => {
      const stageDeals = deals.filter(d => d.current_stage === stage.stage);
      const totalValue = stageDeals.reduce((sum, d) => sum + (d.asking_price || 0), 0);
      return { name: stage.label, stage: stage.stage, value: totalValue, count: stageDeals.length, fill: STAGE_COLORS[stage.stage] || "#888" };
    });
  }, [deals]);

  const funnelData = useMemo(() => {
    const totalDeals = deals.length;
    if (totalDeals === 0) return [];

    const funnelStages = [
      { key: "submission", label: "Submitted" },
      { key: "cim_creation", label: "In Evaluation" },
      { key: "nda_signed", label: "NDA Signed" },
      { key: "loi_signed", label: "LOI Signed" },
      { key: "closing", label: "Closing" },
    ];

    return funnelStages.map((s, idx) => {
      const stageIndex = PIPELINE_STAGES.findIndex(ps => ps.stage === s.key);
      const dealsAtOrPast = deals.filter(d => {
        const dealStageIndex = PIPELINE_STAGES.findIndex(ps => ps.stage === d.current_stage);
        return dealStageIndex >= stageIndex;
      }).length;
      const percentage = totalDeals > 0 ? Math.round((dealsAtOrPast / totalDeals) * 100) : 0;
      return { name: s.label, count: dealsAtOrPast, percentage, fill: COLORS[idx % COLORS.length] };
    });
  }, [deals]);

  const monthlyTrend = useMemo(() => {
    const months = eachMonthOfInterval({ start: subMonths(new Date(), 5), end: new Date() });
    return months.map(month => {
      const monthStart = startOfMonth(month);
      const monthEnd = startOfMonth(addMonths(month, 1));
      const monthDeals = deals.filter(d => {
        const createdAt = new Date(d.created_at);
        return createdAt >= monthStart && createdAt < monthEnd;
      });
      const totalValue = monthDeals.reduce((sum, d) => sum + (d.asking_price || 0), 0);
      return { month: format(month, "MMM"), deals: monthDeals.length, value: totalValue / 1000 };
    });
  }, [deals]);

  const summaryStats = useMemo(() => {
    const totalValue = deals.reduce((sum, d) => sum + (d.asking_price || 0), 0);
    const avgValue = deals.length > 0 ? totalValue / deals.length : 0;
    const closingDeals = deals.filter(d => d.current_stage === 'closing' || d.current_stage === 'loi_signed').length;
    const conversionRate = deals.length > 0 ? Math.round((closingDeals / deals.length) * 100) : 0;
    return { totalValue, avgValue, closingDeals, conversionRate };
  }, [deals]);

  const formatCurrency = (value: number) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={exportAsImage} disabled={isExporting}>
          {isExporting && exportType === "image" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileImage className="h-4 w-4 mr-2" />}
          Export PNG
        </Button>
        <Button variant="outline" size="sm" onClick={exportAsPDF} disabled={isExporting}>
          {isExporting && exportType === "pdf" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
          Export PDF
        </Button>
      </div>

      <div ref={chartsRef} className="space-y-6 bg-background p-4 rounded-lg">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Pipeline Value</p>
                  <p className="text-2xl font-bold">{formatCurrency(summaryStats.totalValue)}</p>
                </div>
                <DollarSign className="h-8 w-8 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-500/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Average Deal Size</p>
                  <p className="text-2xl font-bold">{formatCurrency(summaryStats.avgValue)}</p>
                </div>
                <TrendingUp className="h-8 w-8 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Conversion Rate</p>
                  <p className="text-2xl font-bold">{summaryStats.conversionRate}%</p>
                </div>
                <Target className="h-8 w-8 text-purple-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-orange-500/10 to-orange-600/5 border-orange-500/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Near Closing</p>
                  <p className="text-2xl font-bold">{summaryStats.closingDeals}</p>
                </div>
                <Activity className="h-8 w-8 text-orange-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Deals by Stage</CardTitle>
              <CardDescription>Distribution across pipeline stages</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dealsByStage} layout="vertical" margin={{ left: 20, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 11 }} />
                    <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px" }} formatter={(value: number) => [`${value} deals`, "Count"]} />
                    <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                      {dealsByStage.map((entry, index) => (<Cell key={`cell-${index}`} fill={entry.fill} />))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Value by Stage</CardTitle>
              <CardDescription>Total deal value per pipeline stage</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={valueByStage} layout="vertical" margin={{ left: 20, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                    <XAxis type="number" tickFormatter={(v) => formatCurrency(v)} />
                    <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 11 }} />
                    <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px" }} formatter={(value: number) => [formatCurrency(value), "Value"]} />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                      {valueByStage.map((entry, index) => (<Cell key={`cell-${index}`} fill={entry.fill} />))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Pipeline Funnel</CardTitle>
              <CardDescription>Conversion through key stages</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={funnelData} layout="vertical" margin={{ left: 20, right: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                    <XAxis type="number" domain={[0, 'dataMax']} />
                    <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 11 }} />
                    <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px" }} formatter={(value: number, _name: string, props: any) => [`${value} deals (${props.payload.percentage}%)`, "Count"]} />
                    <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                      {funnelData.map((entry, index) => (<Cell key={`cell-${index}`} fill={entry.fill} />))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Monthly Trend</CardTitle>
              <CardDescription>Deals created over the last 6 months</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={monthlyTrend} margin={{ left: 0, right: 20 }}>
                    <defs>
                      <linearGradient id="colorDeals" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px" }} formatter={(value: number, name: string) => name === "deals" ? [`${value} deals`, "New Deals"] : [`$${value}K`, "Value"]} />
                    <Area type="monotone" dataKey="deals" stroke="#3b82f6" fillOpacity={1} fill="url(#colorDeals)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Stage Distribution</CardTitle>
            <CardDescription>Visual breakdown of deals across all stages</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={dealsByStage.filter(d => d.count > 0)}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="count"
                    nameKey="name"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    labelLine={{ strokeWidth: 1 }}
                  >
                    {dealsByStage.filter(d => d.count > 0).map((entry, index) => (<Cell key={`cell-${index}`} fill={entry.fill} />))}
                  </Pie>
                  <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px" }} formatter={(value: number) => [`${value} deals`, "Count"]} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
